import { type FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { DrawerV2, Notification } from '@nxt-costco-com/forge-components';
import { Alert } from '@nxt-costco-com/forge-icons';
import { AppActionTypes } from 'src/types';
import { hasOptedOut } from 'src/utils/hasOptedOut';
import { AccordionSet } from 'src/components/CookieSettings/AccordionSet';
import { ButtonBox } from 'src/components/CookieSettings/ButtonBox';
import { ExplanationBox } from 'src/components/CookieSettings/ExplanationBox';
import type { SettingsDrawerProps } from 'src/components/CookieSettings/SettingsDrawer/types';
import {
    BODY_SX,
    DEFAULT_HEADER_PROPS,
    DRAWER_BODY_SX,
    DRAWER_SX,
    ERROR_SX
} from 'src/components/CookieSettings/SettingsDrawer/constants';

const scrollAreaSx = {
    flexGrow: 1,
    overflow: 'auto',
    scrollbarWidth: 'thin',
};

export const SettingsDrawer: FC<SettingsDrawerProps> = ({ open, dispatch, state }: SettingsDrawerProps) => {
    const { t } = useTranslation();
    const errorRef = useRef<HTMLDivElement>(null);
    let showAcceptAll = true;
    if (state.trackingConsentDetails) {
        showAcceptAll = hasOptedOut(state.selectedLanguage, state.trackingConsentDetails);
    }

    const drawerHeaderProps = {
        ...DEFAULT_HEADER_PROPS,
        title: t('title'),
        closeButtonAriaLabel: t('closeButtonAriaLabel'),
        onClose: () => {
            if (state.unsavedChanges) {
                dispatch({
                    type: AppActionTypes.UNSAVED_CHANGES,
                    errorMessage: t('errorMessages.unsavedChanges'),
                });
            } else {
                dispatch({ type: AppActionTypes.CLOSE_DRAWER });
            }
        },
        headerSx: {},
    }

    useEffect(() => {
        if(state.isErred && state.errorMessage && errorRef) {
            setTimeout(() => {
                if (errorRef.current) {
                    errorRef.current.focus();
                }
            }, 0);
        }
    }, [state.isErred, state.errorMessage]);

    return (
        <DrawerV2
            anchor="right"
            open={open}
            headerProps={ drawerHeaderProps }
            aria-label={t('title')}
            bodySx={DRAWER_BODY_SX}
            scrollArea='body'
            stickyBarContent={
                state.showConsentUI && <ButtonBox dispatch={dispatch} showAcceptAll={showAcceptAll} />
            }
            sx={DRAWER_SX}
        >
            <Box
                sx={ BODY_SX }
            >
                {state.isErred && state.errorMessage && (
                    <Box sx={ERROR_SX}>
                        <Notification
                            message={state.errorMessage}
                            severity="critical"
                            icon={<Alert />}
                            ref={errorRef}
                        />
                    </Box>
                )}
                <Box
                    sx={ scrollAreaSx }
                >
                    {state.showConsentUI && <ExplanationBox />}
                    {state.showConsentUI && <AccordionSet state={state} dispatch={dispatch} />}
                </Box>
            </Box>
        </DrawerV2>
    );
};
